import { ReactNode } from 'react';
import { useReducer, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addNegativeToast } from 'modules/toastContainer';
import { useGetTemplateHistory } from 'api/TemplateHistoryAPI';
import { useGetTemplateDiff } from 'api/TemplateDiffAPI';
import {
  TemplateDiffContext,
  defaultDiffContextState,
  TemplateDiffContextState,
} from './TemplateDiffContext';
import {
  templateDiffContextReducer,
  dispatchState,
  dispatchCurrentContentIds,
} from './templateDiffContextReducer';

type TemplateDiffContextProviderProps = {
  children: ReactNode;
  /**
   * default context value to use. If not provided, defaultDiffContextState will be used.
   * Providing a param is useful for testing purposes.
   */
  value?: TemplateDiffContextState;
};

export const TemplateDiffContextProvider = ({
  children,
  value = defaultDiffContextState,
}: TemplateDiffContextProviderProps) => {
  const dispatchRedux = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamsTemplateId = searchParams.get('templateId');
  const searchParamContentIdLeft = searchParams.get('contentIdLeft');
  const searchParamContentIdRight = searchParams.get('contentIdRight');

  const [context, contextDispatch] = useReducer(templateDiffContextReducer, value);

  // we can't actually reach this state in the app right now. the backend will return a 404 if
  // the templateId or contentIdLeft or contentIdRight are not found, so we don't need to handle it here.
  // however, this remains a useful thing to throw if we ever need to handle a missing argument in the future.
  // (and typescript yells at us if we don't do this)
  if (
    searchParamsTemplateId === null ||
    searchParamContentIdLeft === null ||
    searchParamContentIdRight === null
  ) {
    const err = new Error(
      'templateId, contentIdLeft, and contentIdRight are required in the URL query parameters',
    );
    throw err;
  }

  const { data, isLoading } = useGetTemplateDiff(
    searchParamsTemplateId,
    searchParamContentIdLeft,
    searchParamContentIdRight,
    {
      onError: (err) => {
        dispatchRedux(addNegativeToast('Failed to fetch template information', err));
      },
    },
  );

  // we display a "current version" tag on a template content diff, if it is indeed the current version
  // for any locale of the template; the current version of the content is whatever will be displayed and editable
  // when the user goes to the edit template page.
  //
  // The diff REST API endpoint does not return this information in a reliable manner, but the template history REST API
  // enddpoint does. So, for now, since those two endpoints are pretty tightly coupled from a UX perspective
  // -- the only way to get to the diff page in our UI is through the history page --
  // I am using the history endpoint to fetch and display that data.
  //
  // In the future, we should just have whatever graphql datasource correctly inform us of this information.
  const { data: templateHistoryData } = useGetTemplateHistory(searchParamsTemplateId, {
    onError: (err) => {
      dispatchRedux(addNegativeToast('Failed to fetch template history', err));
    },
  });

  useEffect(() => {
    contextDispatch(
      dispatchState(data?.contentLeft, data?.contentRight, isLoading, data?.template),
    );
  }, [data, isLoading]);

  useEffect(() => {
    contextDispatch(dispatchCurrentContentIds(templateHistoryData?.templateContentIds));
  }, [templateHistoryData]);

  return <TemplateDiffContext.Provider value={context}>{children}</TemplateDiffContext.Provider>;
};
